import { Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { ogLocale, type Locale } from "@/i18n/config";
import { BASE_URL } from "@/i18n/head";
import { pathFor } from "@/i18n/slugMap";
import { videnStrings } from "@/i18n/content/viden";
import { termPath, type OrdbogTerm } from "@/data/ordbog";
import { breadcrumbList, ldScript } from "@/lib/schema";

export function ordbogTermHead(term: OrdbogTerm, locale: Locale) {
  const t = videnStrings[locale];
  const url = BASE_URL + termPath(term, locale);
  const title = t.termMetaTitle(term.term);

  const definedTerm = {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    name: term.term,
    description: term.short,
    url,
    inDefinedTermSet: BASE_URL + pathFor("viden", locale),
  };

  return {
    meta: [
      { title },
      { name: "description", content: term.short },
      { property: "og:title", content: title },
      { property: "og:description", content: term.short },
      { property: "og:type", content: "article" },
      { property: "og:url", content: url },
      { property: "og:locale", content: ogLocale[locale] },
    ],
    links: [
      { rel: "canonical", href: url },
      { rel: "alternate", hreflang: "da", href: BASE_URL + termPath(term, "da") },
      { rel: "alternate", hreflang: "x-default", href: BASE_URL + termPath(term, "da") },
    ],
    scripts: [
      ldScript(definedTerm),
      ldScript(
        breadcrumbList([
          { name: "MI Support", url: BASE_URL + pathFor("home", locale) },
          { name: t.hero.title, url: BASE_URL + pathFor("viden", locale) },
          { name: term.term, url },
        ]),
      ),
    ],
  };
}

export function OrdbogTermNotFound({ locale }: { locale: Locale }) {
  const t = videnStrings[locale];
  return (
    <SiteLayout>
      <section className="py-24 md:py-32 text-center">
        <div className="mx-auto max-w-2xl px-4 md:px-8">
          <h1 className="text-4xl font-bold">404</h1>
          <Link
            to={pathFor("viden", locale)}
            className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-primary"
          >
            <ArrowLeft className="h-4 w-4" /> {t.backToList}
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}

export function OrdbogTermPage({ term, locale }: { term: OrdbogTerm; locale: Locale }) {
  const t = videnStrings[locale];

  return (
    <SiteLayout>
      <section className="relative overflow-hidden bg-gradient-to-b from-secondary/60 to-background">
        <div className="mx-auto max-w-7xl px-4 md:px-8 py-16 md:py-24">
          <Link
            to={pathFor("viden", locale)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:opacity-80"
          >
            <ArrowLeft className="h-4 w-4" /> {t.backToList}
          </Link>
          <p className="mt-8 text-sm font-semibold uppercase tracking-widest text-primary">
            {t.termEyebrow}
          </p>
          <h1 className="mt-3 text-4xl md:text-6xl font-bold tracking-tight">{term.term}</h1>
          <p className="mt-6 max-w-2xl text-lg md:text-xl text-foreground/85 leading-relaxed">
            {term.short}
          </p>
        </div>
      </section>

      <section className="bg-section-light text-section-light-foreground py-14 md:py-20">
        <div className="mx-auto max-w-3xl px-4 md:px-8 space-y-10">
          {term.sections.map((s, i) => (
            <div key={i}>
              {s.heading && <h2 className="text-2xl md:text-3xl font-bold mb-4">{s.heading}</h2>}
              <p className="text-section-light-foreground/85 leading-relaxed">{s.body}</p>
            </div>
          ))}

          {term.related && (
            <div className="rounded-2xl bg-background/5 border border-section-light-foreground/10 p-7">
              <p className="text-sm font-semibold uppercase tracking-widest text-primary">
                {t.relatedHeading}
              </p>
              <Link
                to={pathFor(term.related.pageId, locale)}
                className="group mt-3 inline-flex items-center gap-2 text-lg font-bold hover:text-primary transition-colors"
              >
                {term.related.label}
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          )}
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-3xl px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold">{t.cta.heading}</h2>
          <p className="mt-4 text-foreground/80 leading-relaxed">{t.cta.body}</p>
          <Link
            to={pathFor("kontakt", locale)}
            className="mt-8 inline-block rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
          >
            {t.cta.button}
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}
